import type { ReactNode } from 'react';
import { Blueprints } from './Blueprints';
import { Clientes } from './Clientes';
import { CustosDeIa } from './CustosDeIa';
import { MasterDashboard } from './MasterDashboard';
import { UsuariosInternos } from './UsuariosInternos';

/**
 * Navegação E rotas do painel Master (`superadminvisual` §5), numa lista só.
 *
 * ⚠️ São a MESMA lista de propósito. Quando eram duas, o menu ganhou um link
 * para `/master/auditoria` que não tinha rota: o item parecia ativo, o clique
 * caía no `*` e voltava para o Dashboard sem explicar nada. Aqui isso não tem
 * como acontecer — item com `para` EXIGE `elemento`, e o TypeScript recusa a
 * compilação se faltar.
 *
 * Duas mudanças em relação ao documento, ambas decididas: "Seleção WhatsApp" e
 * "Metodologia" saíram. Não são "em breve" — são conceitos do produto de
 * recrutamento, de onde o documento veio, e não têm equivalente em eventos.
 */

interface Base {
  rotulo: string;
  /** Classe Tabler sem o prefixo `ti `. */
  icone: string;
}

export type ItemNav =
  | (Base & { emBreve: true; para?: never; elemento?: never; exato?: never })
  | (Base & {
      emBreve?: false;
      para: string;
      elemento: ReactNode;
      /** Casamento exato de rota — só o Dashboard precisa. */
      exato?: boolean;
    });

export interface SecaoNav {
  titulo: string;
  itens: ItemNav[];
}

export const MASTER_NAV: SecaoNav[] = [
  {
    titulo: 'Principal',
    itens: [
      {
        para: '/master',
        rotulo: 'Dashboard',
        icone: 'ti-layout-dashboard',
        exato: true,
        elemento: <MasterDashboard />,
      },
    ],
  },
  {
    titulo: 'Operação',
    itens: [
      {
        para: '/master/clientes',
        rotulo: 'Clientes',
        icone: 'ti-building',
        elemento: <Clientes />,
      },
      {
        para: '/master/blueprints',
        rotulo: 'Blueprints',
        icone: 'ti-template',
        elemento: <Blueprints />,
      },
      { rotulo: 'Onboarding', icone: 'ti-rocket', emBreve: true },
      { rotulo: 'Config. WhatsApp', icone: 'ti-brand-whatsapp', emBreve: true },
      { rotulo: 'Utilização', icone: 'ti-activity', emBreve: true },
    ],
  },
  {
    titulo: 'Financeiro',
    itens: [
      {
        para: '/master/custos-ia',
        rotulo: 'Custos de IA',
        icone: 'ti-cpu',
        elemento: <CustosDeIa />,
      },
      { rotulo: 'Assinaturas', icone: 'ti-credit-card', emBreve: true },
      { rotulo: 'Cobranças', icone: 'ti-receipt', emBreve: true },
    ],
  },
  {
    titulo: 'Atendimento',
    itens: [{ rotulo: 'Suporte', icone: 'ti-headset', emBreve: true }],
  },
  {
    titulo: 'Administração',
    itens: [
      {
        para: '/master/usuarios',
        rotulo: 'Usuários Internos',
        icone: 'ti-users',
        elemento: <UsuariosInternos />,
      },
      // Auditoria da plataforma (visão entre clientes) precisa de runAsPlatform
      // no backend. Enquanto não existe, fica "em breve" — nunca link morto.
      { rotulo: 'Auditoria', icone: 'ti-shield-check', emBreve: true },
      { rotulo: 'LGPD', icone: 'ti-lock', emBreve: true },
      { rotulo: 'Configurações', icone: 'ti-settings', emBreve: true },
    ],
  },
];

/** Só os itens navegáveis, achatados — base das rotas e do breadcrumb. */
const NAVEGAVEIS = MASTER_NAV.flatMap((s) => s.itens).filter(
  (i): i is Extract<ItemNav, { para: string }> => !i.emBreve && i.para !== undefined,
);

/** Rotas-filhas de `/master`, derivadas do menu. */
export const MASTER_ROTAS = NAVEGAVEIS.map((item) =>
  item.para === '/master'
    ? { index: true as const, element: item.elemento }
    : { path: item.para.replace('/master/', ''), element: item.elemento },
);

/** Rótulo da topbar por caminho — mesma fonte, sem terceira lista para desalinhar. */
export const MASTER_BREADCRUMBS: Record<string, string> = Object.fromEntries(
  NAVEGAVEIS.map((i) => [i.para, i.rotulo]),
);
